import { useState } from 'react';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { remiseLigne } from '../../lib/discounts';
import { tr, trf } from '../../lib/i18n';
import { uid } from '../../lib/utils';
import { C } from '../../theme/palette';
import { ArtPicker } from './ArtPicker.jsx';
import { FicheArticleLibre } from './FicheArticleLibre.jsx';

/**
 * « ➕ Ajouter des articles » sur une offre ou une facture déjà faite, sans
 * rouvrir toute la pièce : d'abord « ✍️ Article qui n'est pas dans mon
 * catalogue » (matériel choisi par le client, travaux en plus…), puis un
 * article du catalogue en un toucher.
 *
 * @param {object} props propriétés
 * @param {object[]} props.catalogue articles de l'entreprise
 * @param {Function} [props.setCatalogue] pour garder une ligne manuelle au catalogue
 * @param {object[]} [props.stock] articles du magasin
 * @param {object} props.soc société (unités du métier)
 * @param {boolean} props.estMag pièce du magasin
 * @param {object} [props.client] client de la pièce, pour ses remises
 * @param {string} props.libelleAjout texte du bouton de l'article hors catalogue
 * @param {Function} props.onAjout reçoit chaque ligne ajoutée
 * @returns {import('react').ReactElement} le bouton, puis le panneau
 */
export function AjoutArticles({
  catalogue,
  setCatalogue,
  stock,
  soc,
  estMag,
  client,
  libelleAjout,
  onAjout,
}) {
  const [ouvert, setOuvert] = useState(false);
  const [libre, setLibre] = useState(false);
  const [dernier, setDernier] = useState('');
  const ajoute = (ligne) => {
    onAjout(ligne);
    setDernier(ligne.d);
  };
  return (
    <div style={{ marginTop: 9 }}>
      {!ouvert ? (
        <Btn label="➕ Ajouter des articles" color={C.green} full onClick={() => setOuvert(true)} />
      ) : (
        <Card style={{ padding: 11 }}>
          {libre ? (
            <FicheArticleLibre
              soc={soc}
              estMag={estMag}
              catalogue={catalogue}
              setCatalogue={setCatalogue}
              libelleAjout={libelleAjout}
              onAjout={(l) => {
                ajoute(l);
                setLibre(false);
              }}
              onAnnuler={() => setLibre(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setLibre(true)}
              style={{
                width: '100%',
                border: 'none',
                background: C.navy,
                color: '#fff',
                borderRadius: 11,
                padding: 12,
                fontSize: 12,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {tr('✍️ Article qui n’est pas dans mon catalogue')}
            </button>
          )}
          {dernier && (
            <div
              role="status"
              style={{ fontSize: 10, color: C.green, fontWeight: 800, marginTop: 7 }}
            >
              {trf('✓ {0} ajouté', dernier)}
            </div>
          )}
          <div style={{ borderTop: '1px dashed ' + C.border, marginTop: 10, paddingTop: 9 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: C.ink, marginBottom: 7 }}>
              {estMag
                ? tr('Ou un article du stock — touche pour l’ajouter, puis règle la quantité')
                : tr('Ou un article du catalogue — touche pour l’ajouter, puis règle la quantité')}
            </div>
            <ArtPicker
              catalogue={catalogue}
              stock={stock || []}
              placeholder="🔎 Article, prestation…"
              onPick={(a) => {
                const rem = a.ref ? remiseLigne(client, a.ref, estMag) : 0;
                ajoute({
                  id: uid(),
                  d: a.nom,
                  sd: a.sd || '',
                  u: a.u || (estMag ? 'pièce' : 'm²'),
                  q: 1,
                  p: a.p,
                  rem,
                  ref: a.ref,
                  taxe: true,
                  tva: a.tva != null ? +a.tva : undefined,
                });
              }}
            />
          </div>
          <div style={{ marginTop: 9 }}>
            <Btn
              label="✓ Terminé"
              sm
              color={C.green}
              full
              onClick={() => {
                setOuvert(false);
                setLibre(false);
                setDernier('');
              }}
            />
          </div>
        </Card>
      )}
    </div>
  );
}
